import { ActionSettings, BasicSelect, ICONS, Modal, ToolbarButton } from '@fl/cmsch-fe-library';
import React, { FC, Fragment, memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Ant } from 'common/ant';
import { ConnectedTable } from 'utils/tables';
import { managementOrganizationColumns } from './columns';
import { useManagementOrganizationSetup } from '../../hooks/use-management-organization-setup';
import { adminAction } from '../../model/action';
import { simpleAdminSelector } from '../../model/selector';
import { ManagementOrganizationsTableType } from '../../types/management-organizations';
import { ManagementOrganizationForm } from '../management-organization-form';
const ManagementOrganizationTableBase: FC = _ => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(adminAction.getManagementOrganizationsOptions(''));
  }, [dispatch]);
  const {
    organizationId,
    organizationsOptions,
    managementOrganizationsExtraColumns,
    roleOptions,
    userProfileAndRoles,
    userProfileOptions,
    visibleModal,
    modalTitle,
    organizationDetail,
    isEdit,
    detailHasAccessToAllCenters,
    detailHasAccessToAllFarms,
    handleCloseModal,
    handleColumnOpenCreateModal,
    handleOpenCreateModalWithoutLimitations,
    handleRoleSearch,
    handleRowOpenCreateModal,
    handleSubmitModal,
    handleUserSearch,
    validate,
    validateDebounced,
    t,
    tCommon,
    searchManagementOrganizations,
    onManagementOrganizationsTableFetch,
    handleManagementOrganizationSelectChangeValue,
    removeConfirmTitle,
    removeRolesForUser,
    removeUsersFromRole,
    rowMapper,
    clearOrganizationDetail
  } = useManagementOrganizationSetup();
  const leftHeaderCustomElement = useMemo(() => <BasicSelect value={organizationId} options={organizationsOptions} onFieldChange={handleManagementOrganizationSelectChangeValue} onSearch={searchManagementOrganizations} ignoreSearchMinLength fullWidth testId="organization-select" />, [organizationId, organizationsOptions, handleManagementOrganizationSelectChangeValue, searchManagementOrganizations]);
  const addAccessToUserDisabledTitle = useCallback((_id: number, user: ManagementOrganizationsTableType) => t('cannotCreateRecord', {
    user: user.username
  }), [t]);
  const isAddAccessToUserDisabled = useCallback((_id: number, user: ManagementOrganizationsTableType) => !user.isSubjectMember, []);
  const actionSettings: ActionSettings<ManagementOrganizationsTableType> = useMemo(() => ({
    confirmRowRemove: true,
    extraActions: [{
      id: 'delete',
      role: 'delete',
      callback: removeRolesForUser,
      confirmClick: true,
      confirmTitle: removeConfirmTitle
    }, {
      id: 'create',
      title: t('addAccessToUser'),
      disabledTitle: addAccessToUserDisabledTitle,
      disabled: isAddAccessToUserDisabled,
      role: 'create',
      callback: handleRowOpenCreateModal
    }]
  }), [removeRolesForUser, removeConfirmTitle, addAccessToUserDisabledTitle, isAddAccessToUserDisabled, t, handleRowOpenCreateModal]);
  const headerButtons: Array<ToolbarButton> = useMemo(() => [{
    title: t('access'),
    role: 'create',
    disabled: organizationId.isEmpty,
    disabledTitle: t('needSelectOtherSubject'),
    onClick: handleOpenCreateModalWithoutLimitations
  }], [organizationId, t, handleOpenCreateModalWithoutLimitations]);
  const computedColumns = useMemo(() => organizationId.isFull ? managementOrganizationColumns(managementOrganizationsExtraColumns, t, tCommon, removeUsersFromRole, handleColumnOpenCreateModal) : [], [organizationId.isFull, managementOrganizationsExtraColumns, t, tCommon, removeUsersFromRole, handleColumnOpenCreateModal]);
  return <Fragment>
            <ConnectedTable tableId="managementOrganizations" columns={computedColumns} onFetchTableData={onManagementOrganizationsTableFetch} actionSettings={actionSettings} headerButtons={headerButtons} rowDataToTableRowMapper={rowMapper} rowDataPageSelector={simpleAdminSelector.managementOrganizationsPage} withoutVerticalScrollbar leftHeaderCustomElement={leftHeaderCustomElement} paginationPosition="none" emptyTableMessages={{
      noColumnsVisible: t('needSelectOtherSubject')
    }} emptyTableBodyContent={ICONS.chooseOption} lazyLoadingLimit={20} showAlsoIfEmpty data-sentry-element="ConnectedTable" data-sentry-source-file="index.tsx" />
            {organizationId.isFull && <Modal title={modalTitle} visible={visibleModal} onCancel={handleCloseModal} footer={null}>
                     <Ant.Row>
                         <Ant.Col xs={24}>
                             <ManagementOrganizationForm isEdit={isEdit} dataForEdit={organizationDetail} userProfileOptions={userProfileOptions} roleOptions={roleOptions} userProfileAndRole={userProfileAndRoles} subjectId={organizationId.orCrash('Organization ID not found!')} onRoleSearch={handleRoleSearch} onCancel={handleCloseModal} onSubmit={handleSubmitModal} onUserSearch={handleUserSearch} onSyncValidate={validateDebounced} validate={validate} clearFormValues={clearOrganizationDetail} detailHasAccessToAllCenters={detailHasAccessToAllCenters} detailHasAccessToAllFarms={detailHasAccessToAllFarms} />
                         </Ant.Col>
                     </Ant.Row>
                 </Modal>}
        </Fragment>;
};

/** Organization management table. */
export const ManagementOrganizationTable = memo(ManagementOrganizationTableBase);